import React from 'react';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(8),
    backgroundColor: theme.palette.background.default,
  },
  hero: {
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(2),
    maxWidth: '72ch',
  },
  intro: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    fontSize: '0.95rem',
  },
  list: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    '& li': {
      marginBottom: theme.spacing(0.75),
    },
  },
}));

/** Teachers App: privacy + terms aligned with common Play Store / Data safety disclosure needs. */
function TeacherPolicy() {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Container maxWidth="md" component="article">
        <Box className={classes.hero}>
          <Typography variant="h4" component="h1" gutterBottom>
            Teachers App — Privacy &amp; Policy
          </Typography>
          <Typography className={classes.intro} component="p">
            <strong>Effective date:</strong> May 20, 2026. This document applies to the{' '}
            <strong>SIS Revolution Teachers</strong> mobile application for Android and iOS (&quot;Teachers
            App&quot;). It combines <strong>privacy-related disclosures</strong> (helpful for app store listings
            such as Google Play) with <strong>rules for using the app</strong>. It should be read together with
            our general <a href="/policy">Terms of Use for SIS Revolution</a> and your school&apos;s own notices.
          </Typography>
        </Box>

        <Divider />

        {/* —— Privacy (Play-oriented) —— */}
        <Box className={classes.section} component="section" aria-labelledby="who">
          <Typography id="who" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            1. Who is responsible for personal data?
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            Depending on the information, responsibility may lie with <strong>your school</strong> (or education
            authority), with <strong>SIS Revolution</strong> as provider of the software and hosting, or with
            both under your agreement. In many cases, the school decides <em>what</em> data is collected about
            pupils and staff and <em>why</em>; SIS Revolution processes that data on the school&apos;s
            instructions to provide the service, except where we must process limited account or technical data
            as an independent controller (for example security, billing to the school, or product improvement in
            line with this policy and law).
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            For requests about a <strong>child&apos;s or pupil&apos;s</strong> school record, contact the{' '}
            <strong>school administration</strong> first. For account or privacy questions about the Teachers App
            itself, you may use the contact section at the end.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="collect">
          <Typography id="collect" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            2. Information we collect (categories)
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            The Teachers App is configured by each school. Not every category below will apply to every user. In
            general we may process:
          </Typography>
          <ul className={classes.list}>
            <li>
              <strong>Account and profile data:</strong> for example name, email or phone, staff ID, role, school
              affiliation, login identifiers, and credentials (passwords stored using appropriate hashing—not plain
              text).
            </li>
            <li>
              <strong>Content and school data you enter or access:</strong> lesson materials, marks, attendance,
              comments, messages, timetables, files, or other educational records made available through the
              system—depending on what your school enables.
            </li>
            <li>
              <strong>Device and technical data:</strong> device type, operating system version, app version,
              language, IP address, time zone, and similar diagnostics needed to run the service securely and fix
              errors.
            </li>
            <li>
              <strong>Usage and performance data:</strong> app events (such as crashes, load times, or feature
              usage) where you or we enable analytics or crash reporting—see Section 5.
            </li>
            <li>
              <strong>Communications:</strong> messages you send through in-app channels or to support, and
              notification tokens if push notifications are enabled.
            </li>
          </ul>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="purposes">
          <Typography id="purposes" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            3. How and why we use information
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            We use personal data for purposes such as:
          </Typography>
          <ul className={classes.list}>
            <li>Providing, operating, and maintaining the Teachers App and related services;</li>
            <li>Authenticating users and enforcing permissions set by the school;</li>
            <li>Syncing data between devices and servers; sending service-related notices and notifications;</li>
            <li>Security, fraud prevention, abuse detection, and backup;</li>
            <li>Complying with law, regulatory requests, or valid legal process;</li>
            <li>
              Improving reliability and features (e.g. aggregated or de-identified analytics where appropriate).
            </li>
          </ul>
          <Typography variant="body1" component="div" paragraph>
            Where the GDPR or similar laws apply, we rely on appropriate bases such as <strong>contract</strong>{' '}
            (providing the service), <strong>legitimate interests</strong> (security and improvement, balanced
            against your rights), or <strong>legal obligation</strong>. Schools may rely on{' '}
            <strong>public task</strong> or <strong>legitimate interests</strong> for school administration;
            parents and consent requirements for pupils depend on local law and school policy.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="share">
          <Typography id="share" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            4. Who we share data with
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            We do not sell personal information. We may share data:
          </Typography>
          <ul className={classes.list}>
            <li>
              <strong>With your school:</strong> administrators and other authorised roles as configured in the
              system.
            </li>
            <li>
              <strong>With service providers (&quot;processors&quot;):</strong> hosting, email/SMS, backup, support
              tools, or security vendors—under contracts that require them to protect data and use it only for
              the services we instruct.
            </li>
            <li>
              <strong>For legal reasons:</strong> if required by law, court order, or to protect rights, safety,
              and integrity of users, the service, or the public.
            </li>
            <li>
              <strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets,
              with notice where required by law.
            </li>
          </ul>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="sdks">
          <Typography id="sdks" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            5. Third-party services and SDKs (update to match your app)
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            The Teachers App may incorporate third-party libraries (SDKs) for functions such as maps,
            authentication, push notifications, analytics, or crash reporting. Each SDK may collect or process
            data according to its provider&apos;s terms.
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            <strong>Maintainers must list the actual SDKs used in production builds,</strong> for example (replace
            with your real list):
          </Typography>
          <ul className={classes.list}>
            <li>
              <em>Example:</em> Google Play services / Firebase (Firebase Auth, Cloud Messaging, Analytics,
              Crashlytics)—see Google&apos;s privacy documentation;
            </li>
            <li>
              <em>Example:</em> Other analytics or attribution SDKs—name them and link to their policies.
            </li>
          </ul>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="storage">
          <Typography id="storage" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            6. Storage and international transfers
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            Data may be processed on servers and backup systems chosen for the service—potentially in your
            country or in other countries where we or our subprocessors operate. Where we transfer personal data
            from the EEA, UK, or Switzerland, we use appropriate safeguards (such as standard contractual clauses)
            where required by law.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="retention">
          <Typography id="retention" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            7. How long we keep data
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            Retention depends on the type of data and your school&apos;s settings. Educational records may be kept
            for as long as the school requires for legal or operational purposes. Account data may be removed or
            anonymised after the school ends the subscription or deletes the user, subject to backup cycles and
            legal holds. Technical logs are typically retained for a limited period for security and debugging.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="security">
          <Typography id="security" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            8. Security
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            We implement administrative, technical, and organisational measures appropriate to the risk—including
            encryption in transit where applicable, access controls, monitoring, and staff training. No method of
            transmission or storage is 100% secure; contact us if you believe your account has been compromised.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="students">
          <Typography id="students" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            9. Student data and age
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            The Teachers App is intended for <strong>authorised school staff</strong>, not for general consumer use
            by children. Information about pupils is usually provided or controlled by the school. Schools are
            responsible for compliance with child- and student-privacy rules (such as regional education-privacy
            laws), for obtaining any required consents, and for deciding what data staff may enter or view.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="rights">
          <Typography id="rights" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            10. Your privacy rights
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            Depending on where you live, you may have rights to access, correct, delete, or restrict processing of
            your personal data, to object to certain processing, to data portability, or to lodge a complaint with
            a supervisory authority. To exercise rights regarding <strong>school-held pupil or staff records</strong>, contact your <strong>school</strong> in the first instance. To exercise rights regarding
            data SIS Revolution holds as a service provider, email or write to us using the contact details on{' '}
            <a href="/contact">Contact</a>. We will respond within the timeframes required by law.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="marketing">
          <Typography id="marketing" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            11. Marketing
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            We do not use account data from the Teachers App to send third-party advertising through the app. If
            we ever send optional product news to staff accounts, we will provide a clear opt-out where required
            by law.
          </Typography>
        </Box>

        <Divider />

        {/* —— Terms of use (app behaviour) —— */}
        <Box className={classes.section} component="section" aria-labelledby="eligibility">
          <Typography id="eligibility" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            12. Eligibility and account security
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            You may only use the Teachers App if your school has provisioned you with valid credentials or an
            invitation. Keep login details confidential, use strong passwords where supported, and sign out on
            shared devices. You are responsible for activity on your account until you report unauthorised access
            to your school administrator without undue delay.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="acceptable">
          <Typography id="acceptable" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            13. Acceptable use
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            Use the app only for legitimate education and school purposes, per your school&apos;s policies and
            applicable law. Do not bypass security, access unauthorised data, scrape or export data for unrelated
            commercial use without permission, reverse engineer the app except as the law allows, or introduce
            malware. Report defects or security issues through official support channels.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="payments">
          <Typography id="payments" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            14. Subscriptions and payments
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            School subscriptions are generally billed on the administrative system, not through the Teachers App.
            If any in-app payment or purchase is offered, it is subject to Google Play or Apple App Store rules
            and the purchase screen terms. The Teachers App is not intended for parents&apos; school fee payments
            unless your school explicitly enables separate features for that purpose.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="changes">
          <Typography id="changes" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            15. Changes to this policy
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            We may update this page to reflect product, legal, or regulatory changes. We will post the new version
            here and adjust the effective date. Where changes are material, we may provide additional notice (for
            example in-app or by email to administrators). Continued use after the effective date of an update
            may constitute acceptance where permitted by law.
          </Typography>
        </Box>

        <Divider />
        <Box className={classes.section} component="section" aria-labelledby="contact">
          <Typography id="contact" variant="h6" component="h2" gutterBottom style={{ marginTop: 24 }}>
            16. Contact
          </Typography>
          <Typography variant="body1" component="div" paragraph>
            For pupil or school-record requests, contact your <strong>school administration</strong>. For the
            Teachers App, privacy, or this policy, use our{' '}
            <a href="/contact">Contact</a> page.
          </Typography>
        </Box>

      </Container>
    </div>
  );
}

export default TeacherPolicy;
